import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { BaseSyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  CreateReminderDtoCustomRecurrenceUnitEnum,
  CreateReminderDtoCustomRecurrenceWeekOfMonthEnum,
  CreateReminderDtoNotificationChannelsEnum,
  CreateReminderDtoRecurrenceTypeEnum,
  GetProfileTreatmentDto,
  GetTreatmentDto,
  GetTreatmentOverviewItemDto,
} from 'woofwoof-api';
import AssociatedMedicinesEditor from 'src/sections/@dashboard/profile/components/AssociatedMedicinesEditor';
import TreatmentMaterialsEditor from 'src/sections/@dashboard/profile/components/TreatmentMaterialsEditor';
import UnconfirmedSaveBanner from 'src/sections/@dashboard/warehouse/components/UnconfirmedSaveBanner';
import {
  TreatmentFormSnapshot,
  TreatmentFormValues,
} from './treatmentFormValues';
import {
  TreatmentLinesPayload,
  useTreatmentFormLines,
} from './useTreatmentFormLines';
import { useUnconfirmedTreatmentForm } from './useUnconfirmedTreatmentForm';
import * as Yup from 'yup';
import { translateKey } from 'src/locales/translate';


// ---------- Types ----------
/** `associatedMedicines`, and `materials` in a clinic (`TreatmentLinesPayload`). */
export interface TreatmentFormSubmitValues extends TreatmentLinesPayload {
  name: string;
  description?: string;
  treatmentDate?: string;
  priceNetto?: number;
  priceBrutto?: number;
  vatRate: number;
  isActive: boolean;
  reminderEnabled: boolean;
  reminderDescription?: string;
  reminderScheduledAt?: string;
  reminderRecurrenceType?: CreateReminderDtoRecurrenceTypeEnum;
  reminderCustomRecurrence?: {
    every: number;
    unit: CreateReminderDtoCustomRecurrenceUnitEnum;
    dayOfWeek?: number;
    weekOfMonth?: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum;
  };
  reminderNotificationChannels?: CreateReminderDtoNotificationChannelsEnum[];
}

// ---------- Constants ----------
const VAT_OPTIONS = [
  { value: 23, label: '23%' },
  { value: 8, label: '8%' },
  { value: 5, label: '5%' },
  { value: 0, label: '0%' },
];

const RECURRENCE_OPTIONS = [
  { value: CreateReminderDtoRecurrenceTypeEnum.Single, label: 'Jednorazowo' },
  { value: CreateReminderDtoRecurrenceTypeEnum.Daily, label: 'Codziennie' },
  { value: CreateReminderDtoRecurrenceTypeEnum.Monthly, label: translateKey("hardcoded.miesiecznie") },
  { value: CreateReminderDtoRecurrenceTypeEnum.Yearly, label: 'Rocznie' },
  {
    value: CreateReminderDtoRecurrenceTypeEnum.Custom,
    label: 'Niestandardowe',
  },
];

const CUSTOM_UNIT_OPTIONS = [
  { value: CreateReminderDtoCustomRecurrenceUnitEnum.Hour, label: 'godziny' },
  { value: CreateReminderDtoCustomRecurrenceUnitEnum.Day, label: 'dni' },
  { value: CreateReminderDtoCustomRecurrenceUnitEnum.Week, label: 'tygodnie' },
  { value: CreateReminderDtoCustomRecurrenceUnitEnum.Month, label: translateKey("hardcoded.miesiace") },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 0, label: translateKey("calendar.weekdayLongSun") },
  { value: 1, label: translateKey("calendar.weekdayLongMon") },
  { value: 2, label: translateKey("calendar.weekdayLongTue") },
  { value: 3, label: translateKey("calendar.weekdayLongWed") },
  { value: 4, label: translateKey("calendar.weekdayLongThu") },
  { value: 5, label: translateKey("calendar.weekdayLongFri") },
  { value: 6, label: translateKey("calendar.weekdayLongSat") },
];

const WEEK_OF_MONTH_OPTIONS = [
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.First,
    label: 'Pierwszym',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Second,
    label: 'Drugim',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Third,
    label: 'Trzecim',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Fourth,
    label: 'Czwartym',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Last,
    label: 'Ostatnim',
  },
];

// ---------- Schema ----------
// Module-scope: compiled once per bundle evaluation. Keeps the resolver
// reference stable across renders so react-hook-form doesn't reinit per mount.
const treatmentSchema = Yup.object().shape({
  name: Yup.string()
    .required('Nazwa jest wymagana')
    .max(100, translateKey("hardcoded.nazwa_moze_miec_maksymalnie_100_znakow")),
  description: Yup.string().max(500, translateKey("hardcoded.opis_moze_miec_maksymalnie_500_znakow")),
  treatmentDate: Yup.date()
    .nullable()
    .typeError(translateKey("hardcoded.podaj_prawidlowa_date_i_godzine"))
    .required('Data zabiegu jest wymagana'),
  priceNetto: Yup.mixed(),
  priceBrutto: Yup.mixed(),
  vatRate: Yup.number().required().min(0).max(100),
  isActive: Yup.boolean().required(),
  reminderEnabled: Yup.boolean().required(),
  reminderDescription: Yup.string()
    .default('')
    .when('reminderEnabled', {
      is: true,
      then: (s) => s.required('Opis przypomnienia jest wymagany').max(250),
      otherwise: (s) => s.optional(),
    }),
  reminderScheduledAt: Yup.date()
    .nullable()
    .when('reminderEnabled', {
      is: true,
      then: (s) =>
        s
          .typeError(translateKey("hardcoded.podaj_prawidlowa_date_i_godzine"))
          .required('Data jest wymagana'),
      otherwise: (s) => s.nullable(),
    }),
  reminderRecurrenceType: Yup.string().when('reminderEnabled', {
    is: true,
    then: (s) => s.required('Typ powtarzania jest wymagany'),
    otherwise: (s) => s.optional(),
  }),
  reminderCustomEvery: Yup.number()
    .transform((value) => (isNaN(value) ? 1 : value))
    .default(1),
  reminderCustomUnit: Yup.string().optional(),
  reminderCustomDayOfWeek: Yup.mixed().optional(),
  reminderCustomWeekOfMonth: Yup.mixed().optional(),
  reminderEmailEnabled: Yup.boolean().required(),
  reminderSmsEnabled: Yup.boolean().required(),
});

const treatmentFormResolver = yupResolver(treatmentSchema as any);

type TreatmentFormReminder = NonNullable<
  GetProfileTreatmentDto['linkedReminder']
>;

const hasTreatmentFormReminder = (
  reminder: object | TreatmentFormReminder | undefined,
): reminder is TreatmentFormReminder =>
  Boolean(
    reminder &&
      'description' in reminder &&
      'scheduledAt' in reminder &&
      'recurrenceType' in reminder &&
      'notificationChannels' in reminder,
  );

// ---------- Props ----------
interface ProfileTreatmentFormModalProps {
  isModalOpen: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  treatment?: GetProfileTreatmentDto | GetTreatmentOverviewItemDto;
  canEdit?: boolean;
  isViewOnly?: boolean;
  singleReminderOnly?: boolean;
  isProfessional: boolean;
  organizationId?: string;
  catalogOptions?: GetTreatmentDto[];
  /** A clinic's form: rows keep their stock; only an owner's write strips it. */
  stockEnabled?: boolean;
  /** Its role edits stock (a professional): "Z magazynu" and "Materiały". */
  stockEditable?: boolean;
  /** R3-4: the host's write series is unresolved; the submit resends it. */
  unconfirmedSave?: boolean;
  /** F-8/R-C: that series is held by 422 WAREHOUSE_DISABLED; the banner says so. */
  unconfirmedBlocked?: boolean;
  /** R3-4: that series' snapshot, shown when the form reopens pending. */
  unconfirmedValues?: TreatmentFormSnapshot;
  closeModal: () => void;
  /** `shown`: what the form showed, for the series to keep beside its request. */
  onSubmit: (
    values: TreatmentFormSubmitValues,
    shown: TreatmentFormSnapshot,
  ) => void;
  onDelete: () => void;
}

// ---------- Component ----------
export default function ProfileTreatmentFormModal({
  isModalOpen,
  isCreating,
  isUpdating,
  isDeleting,
  treatment,
  canEdit = true,
  isViewOnly = false,
  singleReminderOnly = false,
  isProfessional,
  organizationId,
  catalogOptions: catalogOptionsProp = [],
  stockEnabled = false,
  stockEditable = true,
  unconfirmedSave = false,
  unconfirmedBlocked = false,
  unconfirmedValues,
  closeModal,
  onSubmit,
  onDelete,
}: ProfileTreatmentFormModalProps) {
  const isEditing = Boolean(treatment);
  const isLoading = isCreating || isUpdating;
  // M-3: like the medication form, a save in flight ignores backdrop/Escape.
  const closeDialog = isLoading ? () => undefined : closeModal;
  // Staff price their clinic's treatments; another clinic's prices never reach
  // them. The owner reads what a clinic charged but prices nothing themselves.
  const pricesHidden =
    !!treatment && 'pricesVisible' in treatment && !treatment.pricesVisible;
  const recordedByClinic =
    !!treatment && 'organizationId' in treatment && !!treatment.organizationId;
  const showPricing = !pricesHidden && (isProfessional || recordedByClinic);

  // Catalog autocomplete state (for professionals)
  const catalogOptions = catalogOptionsProp;
  const [selectedCatalogTreatment, setSelectedCatalogTreatment] =
    useState<GetTreatmentDto | null>(null);

  // Drug lines and materials
  const lines = useTreatmentFormLines({
    facilityWrite: stockEnabled,
    stockEditable,
  });
  const { showSaved, showTemplate, showFrozen } = lines;

  const { control, handleSubmit, reset, watch, setValue, getValues } =
    useForm<TreatmentFormValues>({
      resolver: treatmentFormResolver,
      defaultValues: {
        name: '',
        description: '',
        treatmentDate: new Date(),
        priceNetto: '',
        priceBrutto: '',
        vatRate: 23,
        isActive: true,
        reminderEnabled: false,
        reminderDescription: '',
        reminderScheduledAt: null,
        reminderRecurrenceType: CreateReminderDtoRecurrenceTypeEnum.Single,
        reminderCustomEvery: 1,
        reminderCustomUnit: CreateReminderDtoCustomRecurrenceUnitEnum.Week,
        reminderCustomDayOfWeek: '',
        reminderCustomWeekOfMonth: '',
        reminderEmailEnabled: true,
        reminderSmsEnabled: false,
      },
    });
  const fieldsEditable = canEdit && !unconfirmedSave && !isLoading;

  // Reset form on open
  useEffect(() => {
    if (isModalOpen) {
      if (treatment) {
        const lr = hasTreatmentFormReminder(treatment.linkedReminder)
          ? treatment.linkedReminder
          : undefined;
        reset({
          name: treatment.name,
          description: treatment.description ?? '',
          treatmentDate: treatment.treatmentDate
            ? new Date(treatment.treatmentDate)
            : new Date(),
          priceNetto: treatment.priceNetto ?? '',
          priceBrutto: treatment.priceBrutto ?? '',
          vatRate: treatment.vatRate ?? 8,
          isActive: true,
          reminderEnabled: Boolean(lr),
          reminderDescription: lr?.description ?? '',
          reminderScheduledAt: lr?.scheduledAt
            ? new Date(lr.scheduledAt)
            : null,
          reminderRecurrenceType: singleReminderOnly
            ? CreateReminderDtoRecurrenceTypeEnum.Single
            : (lr?.recurrenceType as unknown as CreateReminderDtoRecurrenceTypeEnum) ??
              CreateReminderDtoRecurrenceTypeEnum.Single,
          reminderCustomEvery: singleReminderOnly
            ? 1
            : lr?.customRecurrence?.every ?? 1,
          reminderCustomUnit: singleReminderOnly
            ? CreateReminderDtoCustomRecurrenceUnitEnum.Week
            : (lr?.customRecurrence
                ?.unit as CreateReminderDtoCustomRecurrenceUnitEnum) ??
              CreateReminderDtoCustomRecurrenceUnitEnum.Week,
          reminderCustomDayOfWeek: singleReminderOnly
            ? ''
            : lr?.customRecurrence?.dayOfWeek ?? '',
          reminderCustomWeekOfMonth: singleReminderOnly
            ? ''
            : (lr?.customRecurrence
                ?.weekOfMonth as CreateReminderDtoCustomRecurrenceWeekOfMonthEnum) ??
              '',
          reminderEmailEnabled:
            lr?.notificationChannels?.includes('EMAIL' as any) ?? true,
          reminderSmsEnabled:
            lr?.notificationChannels?.includes('SMS' as any) ?? false,
        });
        showSaved(treatment);
      } else {
        reset({
          name: '',
          description: '',
          treatmentDate: new Date(),
          priceNetto: '',
          priceBrutto: '',
          vatRate: 23,
          isActive: true,
          reminderEnabled: false,
          reminderDescription: '',
          reminderScheduledAt: new Date(),
          reminderRecurrenceType: CreateReminderDtoRecurrenceTypeEnum.Single,
          reminderCustomEvery: 1,
          reminderCustomUnit: CreateReminderDtoCustomRecurrenceUnitEnum.Week,
          reminderCustomDayOfWeek: '',
          reminderCustomWeekOfMonth: '',
          reminderEmailEnabled: true,
          reminderSmsEnabled: false,
        });
        showSaved(undefined);
        setSelectedCatalogTreatment(null);
      }
    }
  }, [isModalOpen, treatment, reset, singleReminderOnly, showSaved]);
  useUnconfirmedTreatmentForm({
    isModalOpen,
    treatment,
    singleReminderOnly,
    snapshot: unconfirmedSave ? unconfirmedValues : undefined,
    restore: ({ form, catalogTreatment, ...rows }) => {
      reset(form);
      showFrozen(rows);
      setSelectedCatalogTreatment(catalogTreatment);
    },
  });

  // Watch fields
  const reminderEnabled = watch('reminderEnabled');
  const recurrenceType = watch('reminderRecurrenceType');
  const customUnit = watch('reminderCustomUnit');
  const vatRate = watch('vatRate');

  const isCustom =
    recurrenceType === CreateReminderDtoRecurrenceTypeEnum.Custom;
  const isCustomWeekly =
    isCustom && customUnit === CreateReminderDtoCustomRecurrenceUnitEnum.Week;
  const isCustomMonthly =
    isCustom && customUnit === CreateReminderDtoCustomRecurrenceUnitEnum.Month;

  // Set defaults when toggling reminder on
  useEffect(() => {
    if (!reminderEnabled) return;
    if (!watch('reminderScheduledAt')) {
      setValue('reminderScheduledAt', new Date());
    }
    // Auto-fill description from treatment name if empty
    if (!watch('reminderDescription')) {
      const currentName = watch('name');
      if (currentName) {
        setValue('reminderDescription', `Zabieg: ${currentName}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderEnabled]);

  // Price calculation helpers
  const calculateBrutto = useCallback(
    (netto: number, vat: number) =>
      Math.round(netto * (1 + vat / 100) * 100) / 100,
    [],
  );

  const calculateNetto = useCallback(
    (brutto: number, vat: number) =>
      Math.round((brutto / (1 + vat / 100)) * 100) / 100,
    [],
  );

  // Handle catalog selection (professional mode)
  const handleCatalogSelect = useCallback(
    (catalogTreatment: GetTreatmentDto | null) => {
      setSelectedCatalogTreatment(catalogTreatment);
      if (catalogTreatment) {
        setValue('name', catalogTreatment.name);
        setValue('description', catalogTreatment.description ?? '');
        setValue('priceNetto', catalogTreatment.priceNetto ?? '');
        setValue('priceBrutto', catalogTreatment.priceBrutto ?? '');
        setValue('vatRate', catalogTreatment.vatRate ?? 23);
        showTemplate(catalogTreatment);
      }
    },
    [setValue, showTemplate],
  );

  // Form submit handler
  const handleFormSubmit = (values: TreatmentFormValues) => {
    const channels: CreateReminderDtoNotificationChannelsEnum[] = [];
    if (values.reminderEmailEnabled)
      channels.push(CreateReminderDtoNotificationChannelsEnum.Email);
    if (values.reminderSmsEnabled)
      channels.push(CreateReminderDtoNotificationChannelsEnum.Sms);

    const payload: TreatmentFormSubmitValues = {
      name: values.name,
      description: values.description || undefined,
      treatmentDate: values.treatmentDate
        ? (values.treatmentDate as Date).toISOString()
        : undefined,
      priceNetto:
        values.priceNetto !== '' ? Number(values.priceNetto) : undefined,
      priceBrutto:
        values.priceBrutto !== '' ? Number(values.priceBrutto) : undefined,
      vatRate: values.vatRate,
      isActive: values.isActive,
      reminderEnabled: values.reminderEnabled,
      ...lines.toPayload(),
    };

    if (values.reminderEnabled) {
      payload.reminderDescription = values.reminderDescription || undefined;
      payload.reminderScheduledAt = values.reminderScheduledAt
        ? (values.reminderScheduledAt as Date).toISOString()
        : undefined;
      payload.reminderRecurrenceType = singleReminderOnly
        ? CreateReminderDtoRecurrenceTypeEnum.Single
        : values.reminderRecurrenceType;
      payload.reminderNotificationChannels =
        channels.length > 0
          ? channels
          : [CreateReminderDtoNotificationChannelsEnum.Email];

      if (
        !singleReminderOnly &&
        values.reminderRecurrenceType ===
        CreateReminderDtoRecurrenceTypeEnum.Custom
      ) {
        payload.reminderCustomRecurrence = {
          every: values.reminderCustomEvery!,
          unit: values.reminderCustomUnit!,
          dayOfWeek:
            values.reminderCustomDayOfWeek !== ''
              ? values.reminderCustomDayOfWeek
              : undefined,
          weekOfMonth:
            values.reminderCustomWeekOfMonth !== ''
              ? values.reminderCustomWeekOfMonth
              : undefined,
        };
      }
    }

    onSubmit(payload, lines.snapshot(values, selectedCatalogTreatment));
  };

  /** R3-4: no validation — the host resends its frozen request. */
  const resendUnconfirmed = (event?: BaseSyntheticEvent): void => {
    event?.preventDefault();
    handleFormSubmit(getValues());
  };

  return (
    <Dialog open={isModalOpen} onClose={closeDialog} maxWidth="md" fullWidth>
      <form
        onSubmit={
          unconfirmedSave ? resendUnconfirmed : handleSubmit(handleFormSubmit)
        }
      >
        <DialogTitle>
          {isEditing
            ? canEdit
              ? 'Edytuj zabieg'
              : translateKey("hardcoded.podglad_zabiegu")
            : 'Dodaj zabieg'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} pt={1}>
            <UnconfirmedSaveBanner
              pending={unconfirmedSave}
              message={
                unconfirmedBlocked
                  ? translateKey('warehouse.errors.WAREHOUSE_DISABLED')
                  : undefined
              }
            />
            {/* ======== PROFESSIONAL / ORG ADMIN MODE ======== */}
            {isProfessional && !isEditing && (
              <Autocomplete
                options={catalogOptions}
                getOptionLabel={(option) => option.name}
                value={selectedCatalogTreatment}
                onChange={(_, value) => handleCatalogSelect(value)}
                disabled={!fieldsEditable}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Wybierz zabieg z katalogu"
                    placeholder="Wyszukaj zabieg..."
                  />
                )}
                noOptionsText={translateKey("hardcoded.brak_zabiegow_w_katalogu")}
              />
            )}

            {/* Show remaining fields only after catalog selection (create) or always when editing */}
            {(isProfessional
              ? selectedCatalogTreatment || isEditing
              : true) && (
              <>
                {/* ---- Name (always shown in edit; in create: owner only, professionals get it from catalog) ---- */}
                {(isEditing || !isProfessional) && (
                  <Controller
                    name="name"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Nazwa zabiegu"
                        fullWidth
                        disabled={isEditing || !fieldsEditable}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        inputProps={{ maxLength: 100 }}
                      />
                    )}
                  />
                )}

                {/* ---- Description (editable for both, pre-filled from catalog for professionals) ---- */}
                <Controller
                  name="description"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label={translateKey("search.ui.aboutTab")}
                      multiline
                      minRows={2}
                      maxRows={4}
                      fullWidth
                      disabled={!fieldsEditable}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      inputProps={{ maxLength: 500 }}
                    />
                  )}
                />

                {/* ---- Treatment Date ---- */}
                <Controller
                  name="treatmentDate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <MobileDateTimePicker
                      label="Data i godzina zabiegu"
                      value={field.value}
                      onChange={field.onChange}
                      ampm={false}
                      minutesStep={1}
                      disabled={!fieldsEditable}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />

                {/* ---- Pricing (professionals only, read-only from catalog) ---- */}
                {showPricing && (
                  <Stack
                    direction="row"
                    sx={{
                      gap: 3,
                      '@media (max-width: 500px)': {
                        flexDirection: 'column',
                      },
                    }}
                  >
                    <Controller
                      name="priceNetto"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Cena netto"
                          type="number"
                          fullWidth
                          disabled={!fieldsEditable}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">{translateKey("quickBooking.currencySuffix")}</InputAdornment>
                            ),
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          onChange={(e) => {
                            const val = e.target.value;
                            field.onChange(val);
                            if (val !== '') {
                              setValue(
                                'priceBrutto',
                                calculateBrutto(Number(val), vatRate),
                              );
                            }
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="vatRate"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth disabled={!fieldsEditable}>
                          <InputLabel>VAT</InputLabel>
                          <Select
                            {...field}
                            label="VAT"
                            disabled={!fieldsEditable}
                            onChange={(e) => {
                              const newVat = Number(e.target.value);
                              field.onChange(newVat);
                              const netto = watch('priceNetto');
                              if (netto !== '') {
                                setValue(
                                  'priceBrutto',
                                  calculateBrutto(Number(netto), newVat),
                                );
                              }
                            }}
                          >
                            {VAT_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="priceBrutto"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Cena brutto"
                          type="number"
                          fullWidth
                          disabled={!fieldsEditable}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">{translateKey("quickBooking.currencySuffix")}</InputAdornment>
                            ),
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          onChange={(e) => {
                            const val = e.target.value;
                            field.onChange(val);
                            if (val !== '') {
                              setValue(
                                'priceNetto',
                                calculateNetto(Number(val), vatRate),
                              );
                            }
                          }}
                        />
                      )}
                    />
                  </Stack>
                )}

                {/* ---- Associated Medicines (professionals only) ---- */}
                {isProfessional && (
                  <AssociatedMedicinesEditor
                    value={lines.medicines}
                    onChange={lines.setMedicines}
                    disabled={!fieldsEditable}
                    stockEnabled={stockEnabled && stockEditable}
                  />
                )}
                {isProfessional && lines.materialsEditable && (
                  <TreatmentMaterialsEditor
                    value={lines.materials}
                    onChange={lines.setMaterials}
                    disabled={!fieldsEditable}
                  />
                )}

                {/* ---- Reminder Section ---- */}
                <Controller
                  name="reminderEnabled"
                  control={control}
                  render={({ field }) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography>{translateKey("visit.visitCourseMenuReminder")}</Typography>
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={!fieldsEditable}
                      />
                    </Stack>
                  )}
                />

                {reminderEnabled && (
                  <Stack spacing={3}>
                    <Controller
                      name="reminderDescription"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Opis przypomnienia"
                          multiline
                          rows={2}
                          required
                          disabled={!fieldsEditable}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          inputProps={{ maxLength: 250 }}
                        />
                      )}
                    />

                    <Controller
                      name="reminderScheduledAt"
                      control={control}
                      render={({ field, fieldState }) => (
                        <MobileDateTimePicker
                          label="Data i godzina przypomnienia"
                          value={field.value}
                          onChange={field.onChange}
                          ampm={false}
                          minutesStep={1}
                          disabled={!fieldsEditable}
                          minDateTime={new Date()}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={Boolean(fieldState.error)}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      )}
                    />

                    {singleReminderOnly ? (
                      <TextField
                        label="Powtarzanie"
                        value="Jednorazowo"
                        fullWidth
                        InputProps={{ readOnly: true }}
                        inputProps={{
                          'data-testid':
                            'treatment-reminder-recurrence-single',
                        }}
                      />
                    ) : (
                      <Controller
                        name="reminderRecurrenceType"
                        control={control}
                        render={({ field, fieldState }) => (
                          <FormControl
                            fullWidth
                            error={Boolean(fieldState.error)}
                          >
                            <InputLabel>Powtarzanie</InputLabel>
                            <Select
                              {...field}
                              label="Powtarzanie"
                              disabled={!fieldsEditable}
                            >
                              {RECURRENCE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldState.error && (
                              <FormHelperText>
                                {fieldState.error.message}
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                    )}

                    {!singleReminderOnly && isCustom && (
                      <Box>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <Controller
                              name="reminderCustomEvery"
                              control={control}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  label="Co ile"
                                  type="number"
                                  fullWidth
                                  disabled={!fieldsEditable}
                                  inputProps={{ min: 1, max: 365 }}
                                  error={Boolean(fieldState.error)}
                                  helperText={fieldState.error?.message}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={8}>
                            <Controller
                              name="reminderCustomUnit"
                              control={control}
                              render={({ field, fieldState }) => (
                                <FormControl
                                  fullWidth
                                  error={Boolean(fieldState.error)}
                                >
                                  <InputLabel>Jednostka</InputLabel>
                                  <Select
                                    {...field}
                                    label="Jednostka"
                                    disabled={!fieldsEditable}
                                  >
                                    {CUSTOM_UNIT_OPTIONS.map((opt) => (
                                      <MenuItem
                                        key={opt.value}
                                        value={opt.value}
                                      >
                                        {opt.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </Grid>
                          {(isCustomWeekly || isCustomMonthly) && (
                            <Grid item xs={12}>
                              <Controller
                                name="reminderCustomDayOfWeek"
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth>
                                    <InputLabel shrink>{translateKey("hardcoded.dzien_tygodnia_opcjonalnie")}</InputLabel>
                                    <Select
                                      {...field}
                                      label={translateKey("hardcoded.dzien_tygodnia_opcjonalnie")}
                                      displayEmpty
                                      notched
                                      disabled={!fieldsEditable}
                                    >
                                      <MenuItem value="">Dowolny</MenuItem>
                                      {DAY_OF_WEEK_OPTIONS.map((opt) => (
                                        <MenuItem
                                          key={opt.value}
                                          value={opt.value}
                                        >
                                          {opt.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                              />
                            </Grid>
                          )}
                          {isCustomMonthly && (
                            <Grid item xs={12}>
                              <Controller
                                name="reminderCustomWeekOfMonth"
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth>
                                    <InputLabel shrink>{translateKey("hardcoded.tydzien_miesiaca_opcjonalnie")}</InputLabel>
                                    <Select
                                      {...field}
                                      label={translateKey("hardcoded.tydzien_miesiaca_opcjonalnie")}
                                      displayEmpty
                                      notched
                                      disabled={!fieldsEditable}
                                    >
                                      <MenuItem value="">Dowolny</MenuItem>
                                      {WEEK_OF_MONTH_OPTIONS.map((opt) => (
                                        <MenuItem
                                          key={opt.value}
                                          value={opt.value}
                                        >
                                          {opt.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}

                    <FormControl component="fieldset">
                      <Typography>{translateKey("hardcoded.rodzaj_powiadomien")}</Typography>
                      <FormGroup row>
                        <Controller
                          name="reminderEmailEnabled"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.value}
                                  onChange={field.onChange}
                                  disabled={!fieldsEditable}
                                />
                              }
                              label={translateKey("search.book.cardEmail")}
                            />
                          )}
                        />
                        <Controller
                          name="reminderSmsEnabled"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.value}
                                  onChange={field.onChange}
                                  disabled={!fieldsEditable}
                                />
                              }
                              label="SMS"
                            />
                          )}
                        />
                      </FormGroup>
                    </FormControl>
                  </Stack>
                )}
              </>
            )}
          </Stack>

          {!canEdit && !isViewOnly && treatment && (
            <Alert severity="info" sx={{ mt: 2 }}>{translateKey("hardcoded.mozesz_edytowac_tylko_zabiegi_dodane_w_ramach_twojej")}</Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Box>
            {isEditing && canEdit && (
              <LoadingButton
                variant="contained"
                color="error"
                data-testid="treatment-delete-open"
                onClick={onDelete}
                loading={isDeleting}
                disabled={isLoading}
              >{translateKey("common.remove")}</LoadingButton>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={closeModal}
              variant="outlined"
              color="inherit"
              disabled={isLoading || isDeleting}
            >
              {canEdit ? translateKey("common.cancel") : translateKey("common.close")}
            </Button>
            {canEdit && (
              <LoadingButton
                type="submit"
                variant="contained"
                data-testid="treatment-save"
                loading={isLoading}
                disabled={isDeleting}
              >
                {isEditing ? translateKey("common.save") : translateKey("common.add")}
              </LoadingButton>
            )}
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}
